import React, { useState, useEffect } from 'react';
import type { Repository } from '@cowork-gen/core';
import { api } from '../api/client';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateTaskModal({ isOpen, onClose, onCreated }: CreateTaskModalProps) {
  const [repos, setRepos] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    repoId: '',
    storyId: '',
    branch: '',
    maxIterations: 30,
  });

  useEffect(() => {
    if (isOpen) {
      loadRepos();
    }
  }, [isOpen]);

  async function loadRepos() {
    try {
      const response = await api.getRepos();
      if (response.success && response.data) {
        setRepos(response.data);
        if (response.data.length > 0) {
          setFormData((prev) => ({
            ...prev,
            repoId: response.data![0].id,
            branch: response.data![0].defaultBranch,
          }));
        }
      }
    } catch (err) {
      console.error('Failed to load repos:', err);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.repoId || !formData.storyId) return;

    setLoading(true);
    try {
      await api.createTask({
        repoId: formData.repoId,
        storyId: formData.storyId,
        branch: formData.branch || undefined,
        maxIterations: formData.maxIterations,
      });
      onCreated();
      onClose();
      setFormData({ repoId: '', storyId: '', branch: '', maxIterations: 30 });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Create Task</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Repository
            </label>
            <select
              value={formData.repoId}
              onChange={(e) => {
                const repo = repos.find((r) => r.id === e.target.value);
                setFormData((prev) => ({
                  ...prev,
                  repoId: e.target.value,
                  branch: repo?.defaultBranch || '',
                }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select a repository</option>
              {repos.map((repo) => (
                <option key={repo.id} value={repo.id}>
                  {repo.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Story ID
            </label>
            <input
              type="text"
              value={formData.storyId}
              onChange={(e) => setFormData((prev) => ({ ...prev, storyId: e.target.value }))}
              placeholder="US-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch
            </label>
            <input
              type="text"
              value={formData.branch}
              onChange={(e) => setFormData((prev) => ({ ...prev, branch: e.target.value }))}
              placeholder="main"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Iterations
            </label>
            <input
              type="number"
              value={formData.maxIterations}
              onChange={(e) => setFormData((prev) => ({ ...prev, maxIterations: parseInt(e.target.value) || 30 }))}
              min={1}
              max={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.repoId || !formData.storyId}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
